import { useMemo } from "react";
import { Card } from "@app/components/shared/Card";
import { Skeleton } from "@app/components/shared/Skeleton";
import { useOverviewData } from "./hooks/useOverviewData";
import type {
  PortfolioSnapshotItem,
  RecentTrade,
  UserSubscriptionInfo,
  OverviewKpi,
} from "@shared/types/overview";
import "./overview.css";

// ── SVG Icon Components ──────────────────────────────────────────────

const WalletIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
  </svg>
);

const TrendingUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const TrendingDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
    <polyline points="17 18 23 18 23 12"/>
  </svg>
);

const ActivityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ArrowUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/>
    <polyline points="5 12 12 5 19 12"/>
  </svg>
);

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <polyline points="19 12 12 19 5 12"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="5" rx="2"/>
    <line x1="2" x2="22" y1="10" y2="10"/>
  </svg>
);

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
  </svg>
);

const BarChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" x2="12" y1="20" y2="10"/>
    <line x1="18" x2="18" y1="20" y2="4"/>
    <line x1="6" x2="6" y1="20" y2="16"/>
  </svg>
);

const CircleDollarSignIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
    <path d="M12 18V6"/>
  </svg>
);

// ── helpers ───────────────────────────────────────────────────────────

const UNKNOWN_VALUE = "\u2014";

function formatPrice(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return UNKNOWN_VALUE;
  }
  if (value < 0.01) {
    return `$${value.toFixed(8)}`;
  }
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}

function formatSize(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return UNKNOWN_VALUE;
  }
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatRelativeTime(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return UNKNOWN_VALUE;
  }
  const diffSeconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ── Sub-components ────────────────────────────────────────────────────

interface DashboardHeaderProps {
  accountName: string;
  provider: string;
  status: string;
  lastSync: string;
}

function DashboardHeader({ accountName, provider, status, lastSync }: DashboardHeaderProps) {
  const isConnected = status === "active";

  return (
    <header className="dashboard-header">
      <div className="dashboard-header-left">
        <h1 className="dashboard-title">My Dashboard</h1>
        <span className="dashboard-divider">—</span>
        <span className="dashboard-account">{provider} ({accountName})</span>
      </div>
      <div className="dashboard-header-right">
        <span className={`connection-status ${isConnected ? "connected" : "disconnected"}`}>
          {isConnected ? <CheckIcon /> : <XIcon />}
          {isConnected ? "Connected" : "Disconnected"}
        </span>
        <span className="sync-time">{lastSync}</span>
      </div>
    </header>
  );
}

interface KpiCardProps {
  title: string;
  value: string;
  subValue?: string;
  icon: React.ReactNode;
  trend?: "up" | "down" | "neutral";
}

function KpiCard({ title, value, subValue, icon, trend }: KpiCardProps) {
  return (
    <div className="kpi-card-modern">
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-content">
        <span className="kpi-title">{title}</span>
        <span className="kpi-value">{value}</span>
        {subValue && (
          <span className={`kpi-subvalue ${trend || "neutral"}`}>
            {trend === "up" && <ArrowUpIcon />}
            {trend === "down" && <ArrowDownIcon />}
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
}

interface OpenPositionProps {
  item: PortfolioSnapshotItem;
}

function OpenPositionRow({ item }: OpenPositionProps) {
  const direction = item.direction ?? "LONG";
  const leverage = item.leverage ?? 1;
  const pnlValue = item.pnlValue ?? 0;
  const pnlPct = item.pnlPct ?? 0;
  const isProfitable = pnlValue >= 0;
  const hasCurrentPrice = item.currentPrice !== undefined && item.currentPrice !== null;

  return (
    <div className="position-row">
      <div className="position-header">
        <span className="position-symbol">{item.name}</span>
        <span className={`position-direction ${direction.toLowerCase()}`}>
          {direction} {leverage}x
        </span>
      </div>
      <div className="position-details">
        <div className="position-metric">
          <span className="metric-label">Entry</span>
          <span className="metric-value">{formatPrice(item.entryPrice)}</span>
        </div>
        <div className="position-metric">
          <span className="metric-label">Current</span>
          <span className="metric-value">{formatPrice(item.currentPrice)}</span>
        </div>
        <div className="position-metric">
          <span className="metric-label">Size</span>
          <span className="metric-value">{formatSize(item.marketValue)}</span>
        </div>
        <div className={`position-metric pnl ${hasCurrentPrice ? (isProfitable ? "profit" : "loss") : ""}`}>
          <span className="metric-label">P&amp;L</span>
          <span className="metric-value">
            {hasCurrentPrice
              ? `${isProfitable ? "+" : ""}$${Math.abs(pnlValue).toFixed(2)} (${isProfitable ? "+" : ""}${Math.abs(pnlPct).toFixed(1)}%)`
              : UNKNOWN_VALUE}
          </span>
        </div>
      </div>
    </div>
  );
}

interface TradeProps {
  trade: RecentTrade;
}

function TradeRow({ trade }: TradeProps) {
  const isProfit = trade.pnl >= 0;

  return (
    <div className="trade-row">
      <span className="trade-symbol">{trade.symbol}</span>
      <span className="trade-action">{trade.action}</span>
      <span className="trade-price">{formatPrice(trade.price)}</span>
      <span className={`trade-pnl ${isProfit ? "profit" : "loss"}`}>
        {isProfit ? "+" : ""}${Math.abs(trade.pnl).toFixed(2)}
      </span>
      <span className="trade-time">{formatRelativeTime(trade.timestamp)}</span>
    </div>
  );
}

interface SubscriptionSectionProps {
  subscription?: UserSubscriptionInfo;
}

function SubscriptionSection({ subscription }: SubscriptionSectionProps) {
  const status = subscription?.status ?? "none";
  const hasActivePlan = status === "active" && Boolean(subscription?.planName);
  const planLabel = subscription?.planName ?? subscription?.planCode ?? "No active plan";
  const statusLabel = hasActivePlan ? "Active" : status === "expired" ? "Expired" : "Inactive";
  const renewsOn = subscription?.renewsOn ? new Date(subscription.renewsOn) : undefined;
  const renewsOnLabel = renewsOn && !Number.isNaN(renewsOn.getTime())
    ? renewsOn.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : UNKNOWN_VALUE;
  const daysRemaining = subscription?.daysRemaining;

  return (
    <section className="dashboard-section subscription-section">
      <div className="section-header">
        <CreditCardIcon />
        <h2>Subscription</h2>
      </div>
      <Card className="subscription-card">
        <div className="subscription-content">
          <div className="subscription-info">
            <div className="subscription-plan">
              <ZapIcon />
              <span>{planLabel}</span>
            </div>
            <div className="subscription-status">
              <ShieldIcon />
              <span>{statusLabel}</span>
            </div>
          </div>
          <div className="subscription-details">
            <p>Renews on: <strong>{renewsOnLabel}</strong></p>
            {daysRemaining !== undefined && daysRemaining !== null ? (
              <p className="days-left">{daysRemaining} days remaining</p>
            ) : null}
          </div>
        </div>
      </Card>
    </section>
  );
}

// ── KPIs section ──────────────────────────────────────────────────────

const KPI_ICONS: Record<string, React.ReactNode> = {
  totalEquity: <WalletIcon />,
  dailyPnl: <TrendingUpIcon />,
  activeSignals: <BarChartIcon />,
};

function KpiCards({ kpis }: { kpis: OverviewKpi[] }) {
  return (
    <div className="kpi-grid-modern">
      {kpis.map((kpi) => {
        let icon = KPI_ICONS[kpi.id] ?? <CircleDollarSignIcon />;
        let trend: "up" | "down" | "neutral" = "neutral";

        if (kpi.id === "dailyPnl") {
          trend = kpi.value > 0 ? "up" : kpi.value < 0 ? "down" : "neutral";
          icon = kpi.value >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
        }

        const subValue = kpi.delta
          ? `${kpi.delta.formattedValue} ${kpi.delta.periodLabel ?? ""}`.trim()
          : undefined;

        return (
          <KpiCard
            key={kpi.id}
            title={kpi.label}
            value={kpi.formattedValue}
            subValue={subValue}
            icon={icon}
            trend={trend}
          />
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────

export function OverviewTab() {
  const { data, isLoading, isError, error, refetch } = useOverviewData();

  if (isError) {
    return (
      <Card className="overview-card" title="Unable to load overview" bodyClassName="overview-error-card" role="alert">
        <p>{error instanceof Error ? error.message : "An unexpected error occurred while loading the overview data."}</p>
        <button
          type="button"
          onClick={() => { void refetch(); }}
          style={{
            marginTop: "12px",
            padding: "8px 16px",
            border: "1px solid rgba(56,189,248,0.4)",
            borderRadius: "8px",
            background: "rgba(56,189,248,0.1)",
            color: "inherit",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </Card>
    );
  }

  if (!data && isLoading) {
    return (
      <div className="dashboard-container">
        <Skeleton style={{ height: "80px" }} />
        <div className="kpi-grid-modern">
          <Skeleton style={{ height: "120px" }} />
          <Skeleton style={{ height: "120px" }} />
          <Skeleton style={{ height: "120px" }} />
          <Skeleton style={{ height: "120px" }} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="overview-card" bodyClassName="overview-error-card">
        <p>No overview data found. Please check the service configuration.</p>
      </Card>
    );
  }

  const hero = data.hero;
  const accountName = hero.accountName || "\u2014";
  const provider = hero.provider || "\u2014";
  const accountStatus = hero.connectionStatus || "inactive";

  const now = new Date();
  const timeString = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const openPositions = data.portfolio ?? [];
  const openPositionsCount = openPositions.length;
  const recentTrades = data.recentTrades ?? [];

  return (
    <div className="dashboard-container">
      <DashboardHeader
        accountName={accountName}
        provider={provider}
        status={accountStatus}
        lastSync={timeString}
      />

      <KpiCards kpis={data.kpis} />

      {/* Open Positions */}
      <section className="dashboard-section">
        <div className="section-header">
          <ActivityIcon />
          <h2>Open Positions ({openPositionsCount})</h2>
        </div>
        <Card className="positions-card">
          {openPositions.length === 0 ? (
            <div className="empty-state">No open positions at the moment.</div>
          ) : (
            openPositions.map((pos) => (
              <OpenPositionRow key={pos.positionId ?? pos.symbol} item={pos} />
            ))
          )}
        </Card>
      </section>

      {/* Recent Trades */}
      <section className="dashboard-section">
        <div className="section-header">
          <ClockIcon />
          <h2>Recent Trades (Last {recentTrades.length})</h2>
        </div>
        <Card className="trades-card">
          {recentTrades.length === 0 ? (
            <div className="empty-state">No recent trades yet.</div>
          ) : (
            <>
              <div className="trades-header">
                <span>Symbol</span>
                <span>Action</span>
                <span>Price</span>
                <span>P&L</span>
                <span>Time</span>
              </div>
              {recentTrades.map((trade) => (
                <TradeRow key={trade.id} trade={trade} />
              ))}
            </>
          )}
        </Card>
      </section>

      {/* Subscription */}
      <SubscriptionSection subscription={data.subscription} />
    </div>
  );
}
